import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useQuery, useQueryClient} from 'react-query'
import axios from 'axios'
import clsx from 'clsx'
import toaster from '../../../../Utils/toaster'
import {CommonLoading} from '../../../../Utils/commonLoading'
import {CommonPaginationModel} from '../../../../Utils/commonPagination'
import './CustomerMfaSettings.css'

const API_URL = process.env.REACT_APP_API_URL

type CustomerMfaUser = {
  userId: string
  name: string
  email: string
  role: string
  customerName: string
  isActive: boolean
  isMfaEnforced: boolean
}

type CustomerMfaSettings = {
  allUsersMfaEnabled: boolean | null
  mfaRequiredCount: number
  totalUsers: number
  page: number
  pageSize: number
  totalPages: number
  users: CustomerMfaUser[]
}

type UserOverrideMap = Record<string, boolean>

const normalizeCustomerMfaSettings = (raw: Record<string, unknown>): CustomerMfaSettings => ({
  allUsersMfaEnabled:
    (raw.allUsersMfaEnabled as boolean | null | undefined) ??
    (raw.AllUsersMfaEnabled as boolean | null | undefined) ??
    null,
  mfaRequiredCount: Number(raw.mfaRequiredCount ?? raw.MfaRequiredCount ?? 0),
  totalUsers: Number(raw.totalUsers ?? raw.TotalUsers ?? 0),
  page: Number(raw.page ?? raw.Page ?? 1),
  pageSize: Number(raw.pageSize ?? raw.PageSize ?? 10),
  totalPages: Number(raw.totalPages ?? raw.TotalPages ?? 0),
  users: ((raw.users ?? raw.Users ?? []) as Record<string, unknown>[]).map((user) => ({
    userId: String(user.userId ?? user.UserId ?? ''),
    name: String(user.name ?? user.Name ?? ''),
    email: String(user.email ?? user.Email ?? ''),
    role: String(user.role ?? user.Role ?? ''),
    customerName: String(user.customerName ?? user.CustomerName ?? ''),
    isActive: Boolean(user.isActive ?? user.IsActive ?? false),
    isMfaEnforced: Boolean(user.isMfaEnforced ?? user.IsMfaEnforced ?? true),
  })),
})

const fetchCustomerMfa = async (customerId: number, page: number, pageSize: number, search: string) => {
  const {data} = await axios.get<Record<string, unknown>>(`${API_URL}/CustomerSettings/mfa/${customerId}`, {
    params: {page, pageSize, search: search || undefined},
  })
  return normalizeCustomerMfaSettings(data)
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('')
}

function resolveMasterOn(
  allUsersMfaEnabled: boolean | null | undefined,
  mfaRequiredCount: number,
  totalUsers: number
): boolean {
  if (allUsersMfaEnabled === true) return true
  if (allUsersMfaEnabled === false) return false
  if (totalUsers === 0) return true
  // Mixed state (some users exempt): account rule is still ON when anyone requires MFA
  if (mfaRequiredCount > 0) return true
  return false
}

function getEffectiveMfa(
  user: CustomerMfaUser,
  masterOn: boolean,
  masterDirty: boolean,
  overrides: UserOverrideMap
): boolean {
  if (overrides[user.userId] !== undefined) return overrides[user.userId]!

  if (!masterOn) return false

  if (masterDirty) return true

  return user.isMfaEnforced
}

function canEditUserToggle(masterOn: boolean): boolean {
  return masterOn
}

function getServerBaseline(user: CustomerMfaUser): boolean {
  return user.isMfaEnforced
}

export function CustomerMfaSettingsView({customerId}: {customerId: number}) {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [masterOn, setMasterOn] = useState(true)
  const [serverMasterOn, setServerMasterOn] = useState(true)
  const [masterDirty, setMasterDirty] = useState(false)
  const [overrides, setOverrides] = useState<UserOverrideMap>({})
  const [savedSnapshot, setSavedSnapshot] = useState(false)
  const [saving, setSaving] = useState(false)
  const syncedSummaryKeyRef = useRef('')

  const queryKey = useMemo(
    () => ['customer-mfa', customerId, page, rowsPerPage, search],
    [customerId, page, rowsPerPage, search]
  )

  const {data, refetch, isFetching, isError} = useQuery(
    queryKey,
    () => fetchCustomerMfa(customerId, page, rowsPerPage, search),
    {
      enabled: customerId > 0,
      keepPreviousData: true,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    if (isError) toaster('error', 'Failed to load MFA users')
  }, [isError])

  useEffect(() => {
    setPage(1)
    setSearch('')
    setSearchInput('')
    setMasterDirty(false)
    setOverrides({})
    setSavedSnapshot(false)
    syncedSummaryKeyRef.current = ''
  }, [customerId])

  useEffect(() => {
    if (!data) return

    const summaryKey = `${customerId}:${String(data.allUsersMfaEnabled)}:${data.mfaRequiredCount}:${data.totalUsers}`
    if (syncedSummaryKeyRef.current === summaryKey) return

    syncedSummaryKeyRef.current = summaryKey
    const resolvedMaster = resolveMasterOn(
      data.allUsersMfaEnabled,
      data.mfaRequiredCount,
      data.totalUsers
    )
    setServerMasterOn(resolvedMaster)

    if (!masterDirty) setMasterOn(resolvedMaster)
  }, [customerId, data, masterDirty])

  const users = data?.users ?? []
  const totalUsers = data?.totalUsers ?? 0
  const totalPages = Math.max(1, data?.totalPages ?? 1)

  const pendingOverrideCount = useMemo(() => {
    if (masterDirty) return 0
    return Object.keys(overrides).length
  }, [masterDirty, overrides])

  const pendingCount = masterDirty ? 1 : pendingOverrideCount
  const hasPendingChanges = pendingCount > 0

  const requiredSummaryCount = useMemo(() => {
    if (masterDirty) {
      if (!masterOn) return 0
      return totalUsers - Object.values(overrides).filter((value) => value === false).length
    }

    if (data?.mfaRequiredCount != null) return data.mfaRequiredCount

    return users.filter((user) => getEffectiveMfa(user, masterOn, masterDirty, overrides)).length
  }, [data?.mfaRequiredCount, masterDirty, masterOn, overrides, totalUsers, users])

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage)
  }, [])

  const handleRowsPerPageChange = useCallback((nextRowsPerPage: number) => {
    setRowsPerPage(nextRowsPerPage)
    setPage(1)
  }, [])

  const handleSearchSubmit = () => {
    setPage(1)
    setSearch(searchInput.trim())
  }

  const handleMasterToggle = () => {
    setMasterOn((prev) => !prev)
    setMasterDirty(true)
    setOverrides({})
    setSavedSnapshot(false)
  }

  const handleUserToggle = (user: CustomerMfaUser) => {
    if (!canEditUserToggle(masterOn)) return

    const current = getEffectiveMfa(user, masterOn, masterDirty, overrides)
    const next = !current
    const serverValue = getServerBaseline(user)

    setOverrides((prev) => {
      const updated = {...prev}

      if (next === serverValue && !masterDirty) {
        delete updated[user.userId]
      } else {
        updated[user.userId] = next
      }

      return updated
    })
    setSavedSnapshot(false)
  }

  const handleReset = () => {
    setMasterOn(serverMasterOn)
    setMasterDirty(false)
    setOverrides({})
    setSavedSnapshot(false)
  }

  const handleSave = async () => {
    if (!hasPendingChanges) return

    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        applyToAllUsers: false,
        allUsersMfaEnabled: masterOn,
        applyToSelectedUsers: false,
        selectedUsersMfaEnabled: true,
        selectedUserIds: [],
        userOverrides: [] as Array<{userId: string; isMfaEnforced: boolean}>,
      }

      if (masterDirty) {
        payload.applyToAllUsers = true
        payload.allUsersMfaEnabled = masterOn
      } else {
        payload.userOverrides = Object.entries(overrides).map(([userId, isMfaEnforced]) => ({
          userId,
          isMfaEnforced,
        }))
      }

      await axios.put(`${API_URL}/CustomerSettings/mfa/${customerId}`, payload)
      toaster('success', 'MFA settings saved')
      setSavedSnapshot(true)
      setMasterDirty(false)
      setOverrides({})
      syncedSummaryKeyRef.current = ''
      await queryClient.invalidateQueries(['customer-mfa', customerId])
      const refreshed = await refetch()
      if (refreshed.data) {
        const resolvedMaster = resolveMasterOn(
          refreshed.data.allUsersMfaEnabled,
          refreshed.data.mfaRequiredCount,
          refreshed.data.totalUsers
        )
        setMasterOn(resolvedMaster)
        setServerMasterOn(resolvedMaster)
        syncedSummaryKeyRef.current = `${customerId}:${String(refreshed.data.allUsersMfaEnabled)}:${refreshed.data.mfaRequiredCount}:${refreshed.data.totalUsers}`
      }
    } catch {
      toaster('error', 'Failed to save MFA settings')
    } finally {
      setSaving(false)
    }
  }

  const masterHelp =
    'Every user in this account must set up and use a second factor at login. Individual users can be exempted in the table below.'

  const overrideHelp = masterOn
    ? 'Switch a user off to exempt them from the account rule.'
    : 'Turn on the account rule above to manage MFA for all users, or save with the account rule off to disable MFA for everyone.'

  return (
    <div className='customer-mfa-settings'>
      <div className='customer-mfa-settings__header'>
        <div>
          <h4 className='customer-mfa-settings__title'>Multi Factor Authentication</h4>
          <div className='customer-mfa-settings__subtitle'>
            Set one rule for the whole account, then override it for individual users where needed.
          </div>
        </div>
        {totalUsers > 0 && (
          <div className='customer-mfa-settings__summary'>
            <span className='customer-mfa-settings__summary-label'>Required for</span>
            <span className='customer-mfa-settings__summary-value'>
              {requiredSummaryCount} of {totalUsers} users
            </span>
          </div>
        )}
      </div>

      <div className='customer-mfa-settings__master-card'>
        <div className='customer-mfa-settings__master-row'>
          <div>
            <div className='customer-mfa-settings__master-title'>Require MFA for all users in this account</div>
            <div className='customer-mfa-settings__master-help'>{masterHelp}</div>
          </div>
          <button
            type='button'
            className={clsx(
              'customer-mfa-settings__switch',
              masterOn && 'customer-mfa-settings__switch--on'
            )}
            aria-pressed={masterOn}
            aria-label='Require MFA for all users in this account'
            onClick={handleMasterToggle}
          >
            <span className='customer-mfa-settings__switch-knob' />
          </button>
        </div>
        <div
          className={clsx(
            'customer-mfa-settings__master-note',
            masterOn ? 'customer-mfa-settings__master-note--on' : 'customer-mfa-settings__master-note--off'
          )}
        >
          <span className='customer-mfa-settings__master-note-title'>
            {masterOn ? 'Account rule: MFA on.' : 'Account rule: MFA off.'}
          </span>
          <span className='customer-mfa-settings__master-note-body'>
            {masterOn
              ? 'New users inherit this rule automatically.'
              : 'MFA is only required for the users you switch on below.'}
          </span>
        </div>
      </div>

      <div className='customer-mfa-settings__overrides'>
        <div className='customer-mfa-settings__overrides-header'>
          <div>
            <div className='customer-mfa-settings__overrides-title'>Per-user overrides</div>
            <div className='customer-mfa-settings__overrides-help'>{overrideHelp}</div>
          </div>
          <div className='customer-mfa-settings__search-wrap'>
            <span className='customer-mfa-settings__search-icon' aria-hidden='true' />
            <input
              className='customer-mfa-settings__search'
              placeholder='Search name, email or role'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearchSubmit()
              }}
            />
          </div>
        </div>

        <div className='customer-mfa-settings__table-wrap'>
          <table className='customer-mfa-settings__table'>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Customer</th>
                <th>Account</th>
                <th>MFA</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !isFetching && (
                <tr>
                  <td colSpan={5}>
                    <div className='customer-mfa-settings__empty'>No users match this search.</div>
                  </td>
                </tr>
              )}
              {users.map((user, index) => {
                const effective = getEffectiveMfa(user, masterOn, masterDirty, overrides)
                const rowToggleEnabled = canEditUserToggle(masterOn)

                return (
                  <tr key={user.userId || `${user.email}-${index}`}>
                    <td>
                      <div className='customer-mfa-settings__user-cell'>
                        <div className='customer-mfa-settings__avatar'>{getInitials(user.name || user.email)}</div>
                        <div>
                          <div className='customer-mfa-settings__name'>{user.name || '—'}</div>
                          <div className='customer-mfa-settings__email'>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className='customer-mfa-settings__cell-text'>{user.role || 'User'}</span>
                    </td>
                    <td>
                      <span className='customer-mfa-settings__cell-text'>{user.customerName}</span>
                    </td>
                    <td>
                      <span
                        className={clsx(
                          'customer-mfa-settings__status',
                          user.isActive
                            ? 'customer-mfa-settings__status--active'
                            : 'customer-mfa-settings__status--inactive'
                        )}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className='customer-mfa-settings__mfa-cell'>
                        <button
                          type='button'
                          className={clsx(
                            'customer-mfa-settings__switch customer-mfa-settings__switch--sm',
                            effective && 'customer-mfa-settings__switch--on',
                            !rowToggleEnabled && 'customer-mfa-settings__switch--disabled'
                          )}
                          aria-pressed={effective}
                          aria-disabled={!rowToggleEnabled}
                          aria-label={`MFA for ${user.name || user.email}`}
                          disabled={!rowToggleEnabled}
                          onClick={() => handleUserToggle(user)}
                        >
                          <span className='customer-mfa-settings__switch-knob' />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {totalUsers > 0 && (
          <div className='customer-mfa-settings__pagination'>
            <CommonPaginationModel
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              total={totalUsers}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </div>
        )}
      </div>

      {(hasPendingChanges || savedSnapshot) && (
        <div
          className={clsx(
            'customer-mfa-settings__pending',
            savedSnapshot && !hasPendingChanges && 'customer-mfa-settings__pending--saved'
          )}
        >
          <span className='customer-mfa-settings__pending-title'>
            {savedSnapshot && !hasPendingChanges
              ? 'Saved.'
              : `${pendingCount} ${pendingCount === 1 ? 'change' : 'changes'} not saved yet`}
          </span>
          <span className='customer-mfa-settings__pending-detail'>
            {savedSnapshot && !hasPendingChanges
              ? 'MFA settings updated for this account.'
              : 'Nothing changes for these users until you press Save.'}
          </span>
        </div>
      )}

      <div className='customer-mfa-settings__footer'>
        <button
          type='button'
          className='customer-mfa-settings__btn-reset'
          onClick={handleReset}
          disabled={saving || !hasPendingChanges}
        >
          Reset
        </button>
        <button
          type='button'
          className='customer-mfa-settings__btn-save'
          onClick={handleSave}
          disabled={saving || !hasPendingChanges}
        >
          {hasPendingChanges ? 'Save changes' : 'Save'}
        </button>
      </div>

      {isFetching && <CommonLoading />}
    </div>
  )
}
